import type { Carer } from '@lmc/ts-common'
import {
  Button,
  EmptyState,
  NoDataIcon,
  PageHeader,
  PermissionIcon
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import type { TablePaginationConfig } from 'antd'
import type { SorterResult } from 'antd/es/table/interface'
import { useAbilityAccess } from 'components/AccessControl'
import FiltersGroup from 'components/FiltersGroup'
import {
  FiltersInputType,
  type InputItemType
} from 'components/FiltersGroup/utils'
import dayjs from 'dayjs'
import useCarers from 'hooks/useFindItem/useCarers'
import { NAMESPACE } from 'locale'
import { dacConstants } from 'locale/deviceAccessControl'
import { type FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { HashLink } from 'react-router-hash-link'
import styled from 'styled-components'
import { getQueryParams } from 'utils/getQueryParams'
import { Spinner } from '../../../../components/generic'
import { DevicesSignsTable } from './components/DevicesSignsTable'
import { useDeviceSigns, useDevices, useSignTableParams } from './hooks'
import {
  DEVICES_PER_PAGE,
  DEVICES_SETTINGS_PAGE_WITH_HASH,
  type TableParams
} from './types'
import {
  getCarerOptions,
  getDeviceOptions,
  tableParamsDefaultState
} from './utils'

export type Values = [dayjs.Dayjs, dayjs.Dayjs]

const HELP_DISABLED =
  'https://support.logmycare.co.uk/en/articles/8010663-how-do-i-set-up-device-access-control'

const HELP_ENABLED =
  'https://support.logmycare.co.uk/en/articles/8010671-how-do-i-manage-devices-in-my-care-home'

export const DeviceSignsScreen: FC = () => {
  const navigate = useNavigate()

  const { t } = useTranslation(NAMESPACE.DEVICE_ACCESS_CONTROL)

  const { deviceIdParam, userParam, fromParam, toParam } = useSignTableParams()

  const hasRolePermission = useAbilityAccess([{ i: 'update', this: 'Home' }])

  const defaultToDate = dayjs()
  const defaultFromDate = dayjs().subtract(180, 'day')

  const [to, setToDate] = useState<dayjs.Dayjs | null>(
    toParam ? dayjs(toParam) : defaultToDate
  )
  const [from, setFromDate] = useState<dayjs.Dayjs | null>(
    fromParam ? dayjs(fromParam) : defaultFromDate
  )
  const [deviceId, setDeviceId] = useState<string | null>(deviceIdParam)

  const [user, setUser] = useState<string | null>(userParam)

  const [tableParams, setTableParams] = useState<TableParams>(
    tableParamsDefaultState()
  )

  const queryParamsForUrl = {
    ...(deviceId && { deviceId }),
    ...(from && { [`date[from]`]: from?.format('YYYY-MM-DD') }),
    ...(to && { [`date[to]`]: to?.format('YYYY-MM-DD') }),
    ...(user && { user })
  }

  const queryParamsForRequest = {
    ...queryParamsForUrl,
    // @ts-ignore
    [`pagination[itemsPerPage]`]:
      tableParams?.pagination?.pageSize || DEVICES_PER_PAGE,
    [`pagination[page]`]: tableParams?.pagination?.current || 1,
    ...(tableParams?.order && {
      [`pagination[sortOrder]`]: tableParams?.order === 'ascend' ? '1' : '-1'
    }),
    ...(tableParams?.field && {
      [`pagination[sortBy]`]: tableParams?.field
    })
  }

  const { signs, isLoading, count, refetch } = useDeviceSigns(
    queryParamsForRequest,
    hasRolePermission
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const options = {
      pathname: '/organisation/devices/signs',
      search: `?${getQueryParams(queryParamsForUrl)}`
    }
    navigate(options, { replace: true })
    refetch()
  }, [from, to, deviceId, user, tableParams])

  const { devices: allDevices } = useDevices(
    undefined,
    hasRolePermission,
    undefined,
    1000
  )

  const { all: carers } = useCarers({})

  const allCarers = carers?.filter(
    (carer) =>
      !carer?.deleted && !(carer as Carer & { hidden?: boolean })?.hidden
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setTableParams(tableParamsDefaultState(count))
  }, [count])

  const hasSigns = !isLoading && signs?.length

  const handlePaginationChange = (
    pagination: TablePaginationConfig,
    _filters?: unknown,
    sorting?: SorterResult<unknown>
  ) => {
    setTableParams({
      pagination,
      ...sorting
    } as TableParams)
  }

  const filterItems = [
    {
      type: FiltersInputType.DateRange,
      value: [from, to],
      onChange: (values: Values) => {
        setFromDate(values?.[0] ? dayjs(values?.[0]) : null)
        setToDate(values?.[1] ? dayjs(values?.[1]) : null)
      },
      format: 'DD-MM-YYYY',
      placeholder: [t(dacConstants.FROM), t(dacConstants.TO)]
    },
    {
      type: FiltersInputType.Select,
      value: deviceId,
      onChange: (id: string) => setDeviceId(id),
      placeholder: t(dacConstants.DEVICE_NAME),
      options: getDeviceOptions(allDevices)
    },
    {
      type: FiltersInputType.Select,
      value: user,
      onChange: (id: string) => setUser(id),
      placeholder: t(dacConstants.TEAM_MEMBER),
      options: getCarerOptions(allCarers)
    }
  ] as InputItemType[]

  if (isLoading) {
    return <Spinner />
  }

  if (!hasRolePermission) {
    return (
      <Wrapper>
        <PageHeader
          title={t(dacConstants.DEVICES)}
          moreInfoLink={HELP_DISABLED}
        />
        <EmptyState
          title={t(dacConstants.PSST)}
          description={t(dacConstants.NO_PERMISSIONS)}
          icon={<PermissionIcon />}
        />
      </Wrapper>
    )
  }

  return (
    <Wrapper data-testid="device-signs-screen">
      <PageHeader
        title={t(dacConstants.DEVICE_SIGN_INS)}
        onBack={() => navigate(-1)}
        moreInfoLink={HELP_ENABLED}
        testId="device-signs-header"
      />
      <ContentWrapper data-testid="device-signs-content">
        <FiltersGroup items={filterItems} testId="device-signs-filters" />
        {hasSigns ? (
          <TableWrapper data-testid="device-signs-table-wrapper">
            <DevicesSignsTable
              signs={signs}
              isLoading={isLoading}
              handleTableChange={handlePaginationChange}
              tableParams={tableParams}
              testId="device-signs-table"
            />
          </TableWrapper>
        ) : null}
      </ContentWrapper>
      {!hasSigns && (
        <EmptyState
          title={t(dacConstants.CANT_FIND_ANY)}
          description={
            <EmptyStateWrapper data-testid="device-signs-empty-description">
              <Text>{t(dacConstants.WHEN_A_DEVICE)}</Text>
              <HashLink to={DEVICES_SETTINGS_PAGE_WITH_HASH} smooth>
                <StyledNavigateButton
                  variant="text"
                  data-cy="btn-preferences"
                  data-testid="device-signs-preferences-button"
                >
                  {t(dacConstants.PREFERENCES)}
                </StyledNavigateButton>
              </HashLink>
            </EmptyStateWrapper>
          }
          icon={<NoDataIcon />}
          testId="device-signs-empty"
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  margin: 0 auto;
  padding: 24px;
  width: 100%;
  max-width: 1080px;
`

const TableWrapper = styled.div`
  overflow-y: auto;
  margin-top: 24px;
`

const ContentWrapper = styled.div`
  margin-top: 24px;
`

const EmptyStateWrapper = styled.div`
  display: flex;
`

const StyledNavigateButton = styled(Button)`
  margin-left: 4px;
  margin-right: 4px;
`

export default DeviceSignsScreen
